<mat-card class="export-controls-card">
    <mat-card-header>
        <mat-card-title>
            <div class="d-flex align-items-center gap-2">
                <mat-icon>settings</mat-icon>
                <span>Export Configuration</span>
            </div>
        </mat-card-title>
    </mat-card-header>
    <mat-card-content>
        <!-- Export Scope Selection -->
        <div class="section-header mb-3">
          <h6 class="mb-0">Select Export Scope</h6>
          <small class="text-muted">Choose which data to export</small>
        </div>

        <div class="export-options-grid mb-4">
            <!-- Export All -->
            <div class="export-option"
                [class.selected]="selectedScope() === 'all'"
                [class.disabled]="!exportStats.canExportAll">
                <div class="text-center">
                    <p-button 
                        label="All Data"
                        icon="pi pi-database"
                        [outlined]="selectedScope() !== 'all'"
                        [severity]="selectedScope() === 'all' ? 'primary' : 'secondary'"
                        [disabled]="!exportStats.canExportAll"
                        (onClick)="selectScope('all')"
                        class="w-100"
                        styleClass="export-scope-button">
                    </p-button>
                </div>
                <div class="option-details">
                    <div class="record-count">
                        <strong>{{ exportStats.totalRecords }}</strong> records
                    </div>
                    @if(!exportStats.canExportAll) {
                        <small class="text-danger">No data available</small>
                    }
                </div>
            </div>

            <!-- Export Filtered -->
            <div class="export-option" 
               [class.selected]="selectedScope() === 'filtered'"
               [class.disabled]="!exportStats.canExportFiltered"
               [class.hidden]="!exportStats.hasActiveFilters">
                <div class="text-center">
                    <p-button 
                        label="Filtered Data"
                        icon="pi pi-filter"
                        [outlined]="selectedScope() !== 'filtered'"
                        [severity]="selectedScope() === 'filtered' ? 'primary' : 'secondary'"
                        [disabled]="!exportStats.canExportFiltered"
                        (onClick)="selectScope('filtered')"
                        class="w-100"
                        styleClass="export-scope-button">
                    </p-button>
                </div>
                <div class="option-details">
                    <div class="record-count text-primary">
                        <strong>{{ exportStats.filteredRecords }}</strong> records
                    </div>
                    <small class="text-primary">
                        <i class="pi pi-filter"></i> Filters active
                    </small>
                </div>
            </div>

            <!-- Export Selected -->
            <div class="export-option" 
                [class.selected]="selectedScope() === 'selected'"
                [class.disabled]="!exportStats.canExportSelected"
                [class.hidden]="exportStats.selectedRecords === 0">
                <div class="text-center">
                    <p-button 
                        label="Selected Data"
                        icon="pi pi-check-square"
                        [outlined]="selectedScope() !== 'selected'"
                        [severity]="selectedScope() === 'selected' ? 'primary' : 'secondary'"
                        [disabled]="!exportStats.canExportSelected"
                        (onClick)="selectScope('selected')"
                        class="w-100"
                        styleClass="export-scope-button">
                    </p-button>
                </div>
                <div class="option-details">
                    <div class="record-count text-success">
                        <strong>{{ exportStats.selectedRecords }}</strong> records
                    </div>
                    <small class="text-success">
                        <i class="pi pi-check"></i> Selected
                    </small>
                </div>
            </div>

            <!-- Divider -->
            <mat-divider [vertical]="true"></mat-divider>

            <!-- Aggregate Options -->
            @if(exportStats.hasAggregates) {
                <div class="aggregate-section mt-4">
                    <div class="section-header mb-3">
                        <h6 class="mb-0">Summary Rows (Aggregates)</h6>
                        <small class="text-muted">Include totals, averages, and counts</small>
                    </div>
                    
                    <div class="d-flex align-items-center justify-content-between mb-3">
                        <div class="d-flex align-items-center gap-2">
                            <mat-icon class="text-purple-600">functions</mat-icon>
                            <p class="form-label mb-0">Include Aggregates</p>
                        </div>
                        <p-toggleswitch 
                            [(ngModel)]="includeAggregatesValue"
                            (onChange)="onAggregateToggle($event)">
                        </p-toggleswitch>
                    </div>
                
                    @if(includeAggregatesValue()) {
                        <div class="aggregate-options">
                            <div class="mb-3">
                                <p class="form-label">Aggregate Position</p>
                                <div class="d-flex gap-2">
                                    <p-selectButton 
                                        [ngModel]="aggregatePositionValue()"
                                        [options]="aggregatePositionOptions"
                                        (onChange)="onAggregatePositionChange($event)"
                                        styleClass="w-100">
                                        <ng-template let-item pTemplate="item">
                                            <div class="d-flex align-items-center gap-2">
                                                <i [class]="item.icon"></i>
                                                <span>{{ item.label }}</span>
                                            </div>
                                        </ng-template>
                                    </p-selectButton>
                                </div>
                            </div>
                            
                            <div class="form-check">
                                <p-checkbox 
                                    [(ngModel)]="separatorRowValue"
                                    [binary]="true"
                                    (onChange)="onSeparatorRowToggle($event)"
                                    inputId="separator-row">
                                </p-checkbox>
                                <label class="form-check-label ms-2" for="separator-row">
                                    Add separator row between data and aggregates
                                </label>
                            </div>
                        </div>
                    }
                </div>
            } @else {
                <div class="no-aggregates-message mt-4">
                    <div class="d-flex align-items-center gap-2 text-muted">
                        <mat-icon>info</mat-icon>
                        <span>No aggregate columns configured for this grid</span>
                    </div>
                </div>
            }

            <!-- Quick Export Info -->
            <div class="export-summary mt-4 p-3 bg-surface-50 border-round">
                <div class="d-flex align-items-start gap-2">
                    <div><mat-icon class="text-blue-600">info</mat-icon></div>
                    <div class="flex-grow-1">
                        <strong>Export Summary</strong>
                        <ul class="mb-0 mt-2">
                            <li>{{ getExportRecordCount() }} record{{ getExportRecordCount() !== 1 ? 's' : '' }} will be exported</li>
                            <li>{{ exportStats.visibleColumns }} column{{ exportStats.visibleColumns !== 1 ? 's' : '' }} included</li>
                            @if(includeAggregatesValue()) {
                                <li class="text-primary">Summary rows will be added at the {{ aggregatePositionValue() }}</li>
                            }
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    </mat-card-content>
      
    <mat-card-actions class="d-flex justify-content-end gap-2 p-3">
        <p-button 
            label="Quick Export"
            icon="pi pi-bolt"
            severity="success"
            (onClick)="quickExport()"
            [disabled]="!canQuickExport()"
            pTooltip="Export with current settings"
            tooltipPosition="top">
        </p-button>
    </mat-card-actions>
</mat-card>